import React, { useState, useRef, useEffect } from 'react';
import { sendChatbotMessage, getChatbotHistory } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, X, Send, Bot, User, Loader2 } from 'lucide-react';

const Chatbot = () => {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `anon_${Date.now()}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message
      setMessages([{
        type: 'bot',
        text: '¡Hola! Soy el asistente virtual de Ed.Fi. ¿En qué puedo ayudarte hoy?\n\nPuedo responder preguntas sobre:\n• Requisitos para aplicar\n• Tasas y plazos\n• Proceso de solicitud\n• Programas disponibles',
        timestamp: new Date()
      }]);

      // Load history if authenticated
      if (isAuthenticated) {
        loadHistory();
      }
    }
  }, [isOpen, isAuthenticated]);

  const loadHistory = async () => {
    try {
      const response = await getChatbotHistory(20);
      if (response.data && response.data.length > 0) {
        const historyMessages = [];
        response.data.forEach(item => {
          historyMessages.push({
            type: 'user',
            text: item.user_message,
            timestamp: new Date(item.created_at)
          });
          historyMessages.push({
            type: 'bot',
            text: item.bot_response,
            timestamp: new Date(item.created_at)
          });
        });
        setMessages(prev => [...prev, ...historyMessages]);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message
    setMessages(prev => [...prev, {
      type: 'user',
      text: userMessage,
      timestamp: new Date()
    }]);

    setLoading(true);

    try {
      const response = await sendChatbotMessage(userMessage, sessionId);
      
      // Add bot response
      setMessages(prev => [...prev, {
        type: 'bot',
        text: response.data.response,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [...prev, {
        type: 'bot',
        text: 'Lo siento, tuve un problema procesando tu mensaje. Por favor intenta de nuevo.',
        timestamp: new Date(),
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-24 right-6 z-50 w-14 h-14 bg-brand-magenta hover:bg-brand-magenta-dark text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105"
        data-testid="chatbot-toggle"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-44 right-6 z-50 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-fade-up" data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-brand-blue text-white p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold">Asistente Ed.Fi</h3>
              <p className="text-xs text-white/80">Respuestas instantáneas 24/7</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.type === 'user'
                      ? 'bg-brand-blue text-white rounded-br-sm'
                      : msg.error
                      ? 'bg-red-50 text-red-700 rounded-bl-sm'
                      : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {msg.type === 'bot' && (
                      <Bot className="w-4 h-4 mt-1 flex-shrink-0 text-brand-blue" />
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                  </div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-brand-blue" />
                    <span className="text-sm text-slate-600">Escribiendo...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu pregunta..."
                className="flex-1"
                disabled={loading}
                data-testid="chatbot-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className="bg-brand-magenta hover:bg-brand-magenta-dark text-white"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2 text-center">
              Powered by Claude AI
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
